import React, { useMemo } from 'react';

import { fade } from '@material-ui/core';
import { AxisProps } from '@nivo/axes';
import { Theme as ThemeProps } from '@nivo/core';
import { ResponsiveLine } from '@nivo/line';
import sub from 'date-fns/sub';
import cloneDeep from 'lodash/cloneDeep';
import { AutoSizer } from 'react-virtualized';

import { colorsPalette } from '../../theme';
import { calcComplementedPoints, IPoint } from './helpers';
import useStyles from './styles';

const LineChart = ({
  data,
  linear = false,
  axis = false,
  chosen = false,
  groupBy = 'month',
  enableGridY = false,
  enableArea = false,
  from = null,
  to = null,
  name = '',
}) => {
  const classes = useStyles();
  const clonedData = useMemo(() => {
    const dateFrom = from && new Date(from!);
    const dateTo = to && new Date(to!);

    return cloneDeep(data).map((c) => {
      const { data: currentPoints } = c;
      const [pointsBefore = [], pointsAfter = []] = calcComplementedPoints({
        points: currentPoints,
        groupBy,
        from: dateFrom,
        to: dateTo,
      });
      c.data = [...(pointsBefore as IPoint[]), ...currentPoints, ...(pointsAfter as IPoint[])];
      return c;
    });
  }, [data, groupBy, from, to]);

  const isEmpty = clonedData.every((item) => item?.data?.length === 0);
  const monthAgo = sub(new Date(), { months: 1 });
  const today = new Date();

  // Convert input data into dates
  if (clonedData && clonedData.length > 0) {
    clonedData.forEach((d) => {
      if (d && d.data && d.data.length > 0) {
        d?.data.forEach((i) => {
          i.x = new Date(i.x);
        });
      }
    });

    if (chosen) {
      clonedData[0].color = '#6869ED';
    }
  }

  const curve = linear ? 'linear' : 'monotoneX';
  const tickValues = `every ${groupBy}`;

  const axisBottom: AxisProps | null = axis
    ? {
        format: groupBy === 'month' ? `%b '%y` : `%d %b '%y`,
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 20,
        tickRotation: -70,
        legend: '',
        legendOffset: 0,
        legendPosition: 'middle',
        tickValues: tickValues,
      }
    : null;

  const axisLeft: AxisProps | null = axis
    ? {
        orient: 'left',
        tickSize: 5,
        tickPadding: 20,
        tickRotation: 0,
        legend: '',
        legendOffset: -40,
        legendPosition: 'middle',
      }
    : null;

  const theme: ThemeProps = {
    axis: {
      ticks: {
        line: {
          stroke: '#FFFFFF30',
        },
        text: {
          fill: colorsPalette.white,
          fontSize: '14px',
        },
      },
      domain: {
        line: {
          stroke: '#FFFFFF30',
        },
      },
    },
    grid: {
      line: {
        stroke: fade(colorsPalette.white, 0.19),
        strokeWidth: 1,
      },
    },
  };

  const margin = chosen
    ? { top: 20, right: 20, bottom: 110, left: 70 }
    : { top: 10, right: 3, bottom: 3, left: 20 };
  const enablePoints = data && data[0] && data[0]?.data?.length < 2;

  return (
    <AutoSizer>
      {({ height, width }) => {
        return (
          <div style={{ width, height }}>
            <ResponsiveLine
              data-testid={name + 'LineChart'}
              data={clonedData}
              theme={theme}
              margin={margin}
              tooltip={(tooltip) => {
                const point = tooltip?.point;
                const data = point?.data;
                const xFormatted = data?.xFormatted;
                const yFormatted = data?.yFormatted;

                return (
                  <div className={classes.tooltip}>
                    {xFormatted}: {yFormatted}
                  </div>
                );
              }}
              xScale={{
                type: 'time',
                min: isEmpty ? monthAgo : 'auto',
                max: isEmpty ? today : 'auto',
                useUTC: false,
              }}
              xFormat={groupBy === 'month' ? 'time:%B %Y' : 'time:%d %B %Y'}
              yScale={{
                type: 'linear',
                min: 0,
                max: isEmpty ? 100 : 'auto',
                stacked: true,
                reverse: false,
              }}
              curve={curve}
              axisTop={null}
              axisRight={null}
              axisBottom={axisBottom}
              axisLeft={axisLeft}
              enableGridX={false}
              enableGridY={enableGridY}
              colors={(d) => d.color}
              lineWidth={3}
              enablePoints={enablePoints}
              enableCrosshair={false}
              useMesh={true}
              legends={[]}
              enableArea={enableArea}
              gridYValues={4}
            />
          </div>
        );
      }}
    </AutoSizer>
  );
};

export default LineChart;
